
import React, { memo, useEffect, useState } from 'react';
import { CheckCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FormattedDate } from '../utils/formatUtils';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

interface AttendanceStatusProps {
  isAttended: boolean;
  checkedInAt?: string;
  formatDateTime: (date: string) => string;
}

const AttendanceStatus: React.FC<AttendanceStatusProps> = ({
  isAttended,
  checkedInAt,
  formatDateTime
}) => {
  const [highlight, setHighlight] = useState(false);
  const isMobile = useIsMobile();
  
  // Add highlight effect when attendance status changes to true
  useEffect(() => {
    if (isAttended) {
      setHighlight(true);
      const timer = setTimeout(() => {
        setHighlight(false);
      }, 5000); // Keep highlight for 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isAttended, checkedInAt]);
  
  if (isAttended) {
    return (
      <motion.div 
        className={`flex ${isMobile ? 'flex-row items-center' : 'flex-col items-end'} gap-1`}
        initial={{ scale: highlight ? 1 : 1 }}
        animate={{ 
          scale: highlight ? [1, 1.05, 1] : 1,
          backgroundColor: highlight ? ['transparent', 'rgba(34, 197, 94, 0.2)', 'transparent'] : 'transparent'
        }}
        transition={{ duration: highlight ? 2 : 0.5, ease: "easeInOut" }}
      >
        <Badge 
          variant="default" 
          className={`flex items-center ${isMobile ? 'px-2 py-1 text-xs' : 'px-4 py-1.5 rounded-full'} ${highlight ? 'bg-green-500 shadow-md animate-pulse-subtle' : 'bg-green-500'} hover:bg-green-600 shadow-sm transition-all duration-500`}
        >
          <CheckCircle size={isMobile ? 12 : 16} className={isMobile ? "mr-1" : "mr-1.5"} />
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>
            {isMobile ? 'Đã check-in' : 'Đã check-in'}
          </span>
        </Badge>
        {!isMobile && checkedInAt && (
          <div className="flex items-center justify-end space-x-1 text-xs text-green-600 dark:text-green-400 mt-0.5">
            <Clock size={12} />
            <span title={formatDateTime(checkedInAt)} className="cursor-help hover:underline font-medium">
              <FormattedDate date={checkedInAt} formatter={formatDateTime} />
            </span>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <Badge 
      variant="outline" 
      className={`text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600 ${isMobile ? 'text-xs px-2 py-1' : ''} hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all`}
    >
      {isMobile ? 'Chưa check-in' : 'Chưa check-in'}
    </Badge>
  );
};

export default memo(AttendanceStatus);
